import { useState, useMemo, useEffect } from 'react';
import { isSameDay } from 'date-fns';
import { MenuItem, InlineSelect, Tooltip, Typography, useStateInRef, WithInfoIcon, KeyboardDatePicker } from 'seagull';
import { useIoContext } from "../../../contexts";
import { useOnMount } from "../../../hooks/useOnMount";
import { serviceModel } from "../../../models";
import { DiscountExpirationType, LimitedTimeDiscountOfferModel } from "../../../models/LimitedTimeDiscountOfferModel";
import { validateLimitedTimeDiscountOffer } from "../../../models/service";
import { fromDateStampObject, toDateStampObject } from "../../../models/time";
import pluralize from "../../../utils/pluralize";
import { AdvancedSettingsControl } from "../AdvancedSettingsControl/AdvancedSettingsControl";
import styles from "../ServiceAdvancedSettings.module.scss";
import { AdvancedSetting } from "../ServiceAdvancedSettings.types";
import { getInitialLimitedTimeDiscountOfferState } from "../ServiceAdvancedSettings.utils";
import { getDaysOptions, getDateRange } from "./LimitedTimeDiscountOfferControl.utils";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export function LimitedTimeDiscountOfferControl(props) {
  const {
    service,
    info,
    label,
    isReadOnly,
    onLimitedTimeDiscountOfferChange,
    disabled,
    testId: testPrefix,
    relationshipState,
    disabledTooltip,
    onToggle
  } = props;
  const {
    getNow
  } = useIoContext();
  const getProps = useStateInRef(props);
  const now = useMemo(() => getNow(), []);
  if (!onLimitedTimeDiscountOfferChange || relationshipState.type !== 'awaitingApproval') {
    return null;
  }
  const limitedTimeDiscountOffer = serviceModel.limitedTimeDiscountOffer(service);
  const discount = useMemo(() => service.cost.discount, [service.cost.discount]);
  const initialState = useMemo(() => getInitialLimitedTimeDiscountOfferState({
    now,
    existingOffer: limitedTimeDiscountOffer
  }), [now, limitedTimeDiscountOffer]);
  const [timeframeType, setTimeframeType] = useState(initialState.timeframeType);
  const [daysCount, setDaysCount] = useState(initialState.daysCount);
  const [selectedDate, setSelectedDate] = useState(initialState.selectedDate);
  const [error, setError] = useState('');
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  useEffect(() => {
    // reset state when limitedTimeDiscountOffer is undefined
    if (!limitedTimeDiscountOffer) {
      const defaultState = getInitialLimitedTimeDiscountOfferState({
        now
      });
      setTimeframeType(defaultState.timeframeType);
      setDaysCount(defaultState.daysCount);
      setSelectedDate(defaultState.selectedDate);
    }
  }, [limitedTimeDiscountOffer, now]);
  useOnMount(() => {
    if (limitedTimeDiscountOffer) {
      const expirationType = limitedTimeDiscountOffer.DiscountExpirationType();
      const type = expirationType === DiscountExpirationType.Date ? 'until' : 'within';
      setTimeframeType(type);
      if (type === 'within') {
        const days = limitedTimeDiscountOffer.ExpirationDays();
        if (days !== undefined) {
          setDaysCount(days);
        }
      } else {
        const date = limitedTimeDiscountOffer.ExpirationDate();
        if (date) {
          setSelectedDate(fromDateStampObject(date));
        }
      }
    }
  });
  useEffect(() => {
    const error = validateLimitedTimeDiscountOffer(service, now);
    setError(error);
    if (!service.cost.discount) {
      const defaultState = getInitialLimitedTimeDiscountOfferState({
        now: getNow()
      });
      setTimeframeType(defaultState.timeframeType);
      setDaysCount(defaultState.daysCount);
      setSelectedDate(defaultState.selectedDate);
      if (serviceModel.limitedTimeDiscountOffer(service)) {
        var _getProps$onLimitedTi, _getProps;
        (_getProps$onLimitedTi = (_getProps = getProps()).onLimitedTimeDiscountOfferChange) === null || _getProps$onLimitedTi === void 0 || _getProps$onLimitedTi.call(_getProps, service.id, undefined);
      }
    }
  }, [service, now, getNow]);
  const dayOptions = useMemo(() => {
    return getDaysOptions();
  }, []);
  const {
    minDate,
    maxDate
  } = useMemo(() => {
    return getDateRange(now);
  }, [now]);
  const getExpirationByTimeframe = timeframe => {
    if (timeframe === 'within') {
      return {
        type: DiscountExpirationType.Days,
        days: daysCount
      };
    } else {
      return {
        type: DiscountExpirationType.Date,
        date: toDateStampObject(selectedDate)
      };
    }
  };
  const handleChange = enabled => {
    onToggle(enabled);
  };
  const handleTimeframeTypeChange = newType => {
    setTimeframeType(newType);
    const expiration = getExpirationByTimeframe(newType);
    if (limitedTimeDiscountOffer && discount) {
      const updatedOffer = new LimitedTimeDiscountOfferModel(expiration);
      onLimitedTimeDiscountOfferChange === null || onLimitedTimeDiscountOfferChange === void 0 || onLimitedTimeDiscountOfferChange(service.id, updatedOffer);
    }
  };
  const handleDaysChange = newDays => {
    setDaysCount(newDays);
    if (timeframeType === 'within') {
      const currentOffer = limitedTimeDiscountOffer;
      if (currentOffer) {
        const updatedOffer = currentOffer.setExpirationDays(newDays);
        onLimitedTimeDiscountOfferChange === null || onLimitedTimeDiscountOfferChange === void 0 || onLimitedTimeDiscountOfferChange(service.id, updatedOffer);
      }
    }
  };
  const handleDateChange = newDate => {
    setSelectedDate(newDate);
    if (timeframeType === 'until') {
      const currentOffer = limitedTimeDiscountOffer;
      if (currentOffer) {
        const updatedOffer = currentOffer.setExpirationDate(toDateStampObject(newDate));
        onLimitedTimeDiscountOfferChange === null || onLimitedTimeDiscountOfferChange === void 0 || onLimitedTimeDiscountOfferChange(service.id, updatedOffer);
      }
    }
  };
  const shouldShowDefaultView = disabled || !limitedTimeDiscountOffer;
  const adjustedMinDate = useMemo(() => {
    return selectedDate < minDate ? selectedDate : minDate;
  }, [selectedDate, minDate]);
  return /*#__PURE__*/_jsx(AdvancedSettingsControl, {
    info: shouldShowDefaultView ? info : '',
    infoTestId: "".concat(testPrefix, "-limited-time-discount-info"),
    disabled: disabled,
    label: shouldShowDefaultView ? label : '',
    isReadOnly: isReadOnly,
    checked: !disabled && !!limitedTimeDiscountOffer,
    testid: "".concat(testPrefix, "-limited-time-discount"),
    disabledTooltip: shouldShowDefaultView ? disabledTooltip : '',
    onChange: handleChange,
    labelSuffix: !shouldShowDefaultView && /*#__PURE__*/_jsx(WithInfoIcon, {
      className: styles.rowAndIconContainer,
      info: info,
      infoTestId: "".concat(testPrefix, "-limited-time-discount-info"),
      children: /*#__PURE__*/_jsxs("div", {
        className: styles.rowContent,
        children: [/*#__PURE__*/_jsx(Typography, {
          children: "Apply discount only if the proposal is accepted"
        }), /*#__PURE__*/_jsxs(InlineSelect, {
          skin: "primary",
          disabled: disabled,
          value: timeframeType,
          testid: "".concat(testPrefix, "-timeframe-type"),
          onChange: e => handleTimeframeTypeChange(e.target.value),
          children: [/*#__PURE__*/_jsx(MenuItem, {
            value: "within",
            children: "within"
          }), /*#__PURE__*/_jsx(MenuItem, {
            value: "until",
            children: "until"
          })]
        }), timeframeType === 'within' ? /*#__PURE__*/_jsx(InlineSelect, {
          skin: "primary",
          disabled: disabled,
          value: daysCount,
          testid: "".concat(testPrefix, "-days-count"),
          onChange: e => handleDaysChange(Number(e.target.value)),
          children: dayOptions.map(day => /*#__PURE__*/_jsx(MenuItem, {
            value: day.toString(),
            "data-testid": "".concat(testPrefix, "-days-count-option-").concat(day),
            children: pluralize('day', day, true)
          }, day))
        }) : /*#__PURE__*/_jsx(Tooltip, {
          title: isCalendarOpen ? '' : error,
          placement: "top",
          children: /*#__PURE__*/_jsx("div", {
            style: {
              position: 'relative'
            },
            onFocus: () => setIsCalendarOpen(true),
            onBlur: () => setIsCalendarOpen(false),
            children: /*#__PURE__*/_jsx(KeyboardDatePicker, {
              style: {
                width: 160,
                textAlign: 'center'
              },
              size: "small",
              "data-testid": "".concat(testPrefix, "-deadline-date"),
              className: styles.noShrink,
              value: selectedDate,
              minDate: adjustedMinDate,
              maxDate: maxDate,
              disabled: disabled,
              disableErrors: !error,
              helperText: '',
              error: !!error,
              onChange: newDate => newDate && handleDateChange(newDate),
              shouldDisableDate: date => !!date && (date < minDate || date > maxDate) && !isSameDay(date, selectedDate)
            })
          })
        })]
      })
    })
  }, AdvancedSetting.LimitedTimeDiscountOffer);
}